<script>
import { MESSAGE_TYPE } from 'widget/helpers/constants';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { resolveLidMentions } from 'shared/helpers/lidMentions.js';
import { ATTACHMENT_ICONS } from 'shared/constants/messages';

export default {
  name: 'MessagePreview',
  props: {
    message: {
      type: Object,
      required: true,
    },
    showMessageType: {
      type: Boolean,
      default: true,
    },
    defaultEmptyMessage: {
      type: String,
      default: '',
    },
    // Active search term (inbox-rail search). When it appears in the preview
    // text the snippet is windowed to keep the match visible and every
    // occurrence is emphasised, WhatsApp-style.
    highlightTerm: {
      type: String,
      default: '',
    },
  },
  setup() {
    const { getPlainText } = useMessageFormatter();
    return {
      getPlainText,
    };
  },
  computed: {
    messageByAgent() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.OUTGOING;
    },
    isMessageAnActivity() {
      const { message_type: messageType } = this.message;
      return messageType === MESSAGE_TYPE.ACTIVITY;
    },
    isMessagePrivate() {
      const { private: isPrivate } = this.message;
      return isPrivate;
    },
    parsedLastMessage() {
      const { content_attributes: contentAttributes } = this.message;
      const { email: { subject } = {} } = contentAttributes || {};
      return resolveLidMentions(
        this.getPlainText(subject || this.message.content)
      );
    },
    lastMessageFileType() {
      const [{ file_type: fileType } = {}] = this.message.attachments;
      return fileType;
    },
    attachmentIcon() {
      return ATTACHMENT_ICONS[this.lastMessageFileType];
    },
    attachmentMessageContent() {
      return `CHAT_LIST.ATTACHMENTS.${this.lastMessageFileType}.CONTENT`;
    },
    isMessageSticker() {
      return this.message && this.message.content_type === 'sticker';
    },
    // Search highlight, WhatsApp-style: null unless the term occurs in the
    // preview text. The single-line CSS ellipsis would hide a match that sits
    // deep in a long message, so the snippet is re-windowed to start a few
    // words before the FIRST match ("…context MATCH rest"). Segments render
    // through plain interpolation — never v-html — so message content cannot
    // inject markup.
    highlightSegments() {
      const term = (this.highlightTerm || '').trim();
      if (!term || !this.message.content) return null;
      const text = this.parsedLastMessage || '';
      const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matcher = new RegExp(escapedTerm, 'gi');
      const firstMatch = matcher.exec(text);
      if (!firstMatch) return null;

      // Keep ~24 chars of lead-in context, snapped to the next word start.
      const LEAD = 24;
      let start = 0;
      if (firstMatch.index > LEAD) {
        start = firstMatch.index - LEAD;
        const wordStart = text.indexOf(' ', start);
        if (wordStart !== -1 && wordStart < firstMatch.index) {
          start = wordStart + 1;
        }
        // Never cut through a surrogate pair (emoji) at the window edge.
        const code = text.charCodeAt(start);
        if (code >= 0xdc00 && code <= 0xdfff) start += 1;
      }
      // 240 chars is plenty for one preview line; CSS ellipsis trims the tail.
      const windowed = text.slice(start, start + 240);

      const segments = [];
      if (start > 0) segments.push({ text: '…', hit: false });
      let cursor = 0;
      matcher.lastIndex = 0;
      let match = matcher.exec(windowed);
      while (match) {
        if (match.index > cursor) {
          segments.push({
            text: windowed.slice(cursor, match.index),
            hit: false,
          });
        }
        segments.push({ text: match[0], hit: true });
        cursor = match.index + match[0].length;
        match = matcher.exec(windowed);
      }
      if (cursor < windowed.length) {
        segments.push({ text: windowed.slice(cursor), hit: false });
      }
      return segments;
    },
  },
};
</script>

<template>
  <div class="overflow-hidden text-ellipsis whitespace-nowrap">
    <template v-if="showMessageType">
      <fluent-icon
        v-if="isMessagePrivate"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="lock-closed"
      />
      <fluent-icon
        v-else-if="messageByAgent"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="arrow-reply"
      />
      <fluent-icon
        v-else-if="isMessageAnActivity"
        size="16"
        class="-mt-0.5 align-middle text-n-slate-11 inline-block"
        icon="info"
      />
    </template>
    <span v-if="message.content && isMessageSticker">
      <fluent-icon
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11"
        icon="image"
      />
      {{ $t('CHAT_LIST.ATTACHMENTS.image.CONTENT') }}
    </span>
    <span v-else-if="message.content && highlightSegments">
      <span
        v-for="(segment, index) in highlightSegments"
        :key="index"
        :class="segment.hit ? 'text-n-teal-11 font-semibold' : ''"
      >
        {{ segment.text }}
      </span>
    </span>
    <span v-else-if="message.content">
      {{ parsedLastMessage }}
    </span>
    <span v-else-if="message.attachments">
      <fluent-icon
        v-if="attachmentIcon && showMessageType"
        size="16"
        class="-mt-0.5 align-middle inline-block text-n-slate-11"
        :icon="attachmentIcon"
      />
      {{ $t(`${attachmentMessageContent}`) }}
    </span>
    <span v-else>
      {{ defaultEmptyMessage || $t('CHAT_LIST.NO_CONTENT') }}
    </span>
  </div>
</template>
